import React from "react";
import PropTypes from "prop-types";
import CoverPage from "./CoverPage.jsx";
import ManifestPage from "./ManifestPage.jsx";
import useReportMetrics from "../metrics/useReportMetrics.js";

/**
 * BoardReportPages - pure presentational assembly of a board report's pages:
 * a CoverPage followed by one ManifestPage per `manifest.pages`.
 *
 * Derives the exact same title/period/narrative/drivers/subtitle/
 * confidentiality/reportOwner props BoardReportPrint used to compute inline,
 * and passes them through with the same `pageNumber`/`totalPages`/
 * `generatedAt` shape. Carries no data-loading, print-mode, or PDF-completion
 * concerns of its own - those stay on the caller (BoardReportPrint today;
 * an interactive browser preview later) so this component can be reused by
 * both without dragging along print-only machinery.
 *
 * SP5 seam: optional `narrative`/`drivers` override props let a caller (the
 * two-pane narrative editor) show in-progress edits instead of the
 * persisted `run.narrative`/`run.drivers`. Both are undefined-checked
 * (`!== undefined`, not truthiness) so an intentional falsy override (e.g.
 * an empty array) still wins over `run`. Neither BoardReportPrint nor the
 * SP4 route preview ever passes them, so their behavior - always deriving
 * from `run` - is unchanged.
 *
 * SP6 seam: this component OWNS `useReportMetrics(boardReportId, runId)` and forwards its
 * `metrics`/`metricsLoading` to every ManifestPage (and on to its blocks) so the narrative blocks
 * can resolve `{{fig:KEY}}` @figure tokens (renderNarrative.jsx) - and, for the PDF path, so the
 * chromedp poller waits on `metricsLoading` for any block whose narrative contains a token. Since
 * BOTH BoardReportPrint and BoardReportPreview render through this component, wiring the hook
 * here (rather than in either caller) is the single place @figures resolve for print AND the
 * embedded preview - neither caller needs any metrics-specific knowledge of its own. The hook is
 * react-query-cached (useBlockData), so a caller that ALSO reads report metrics for its own UI
 * (e.g. BoardReportReview's inspector) shares the same cache entries - no extra network traffic.
 *
 * SP6 seam: optional `selectable`/`selectedBlockId`/`onSelectBlock` props are forwarded straight
 * through to every ManifestPage, which only wraps its blocks in the selection affordance when
 * `selectable` is truthy (see ManifestPage's doc comment). A caller that never passes these props
 * (BoardReportPrint, the SP4 standalone preview) gets the bare, pre-SP6 block markup.
 */
const BoardReportPages = ({
  boardReport,
  run,
  manifest,
  boardReportId,
  runId,
  narrative,
  drivers,
  selectable,
  selectedBlockId,
  selectedBinding,
  onSelectBlock,
  hideCover,
}) => {
  const title = boardReport?.name || "Board Report";
  const period = run?.period || boardReport?.period || "";
  const effectiveNarrative = narrative !== undefined ? narrative : run?.narrative;
  const effectiveDrivers = drivers !== undefined ? drivers : run?.drivers;
  const subtitle = boardReport?.subtitle;
  const confidentiality = boardReport?.confidentiality;
  const reportOwner = boardReport?.report_owner;
  const { metrics, isLoading: metricsLoading } = useReportMetrics(boardReportId, runId);

  const manifestPages = manifest?.pages ?? [];
  const totalPages = 1 + manifestPages.length; // cover + manifest pages

  return (
    <>
      {/* hideCover: the in-app editing canvas (chromeless preview) skips the cover — it has nothing
          to edit and is mostly whitespace, so landing on it makes a slow figure load feel like an
          empty screen. The print/PDF path never passes hideCover, so the exported report keeps its
          cover page. */}
      {!hideCover && (
        <CoverPage
          title={title}
          subtitle={subtitle}
          confidentiality={confidentiality}
          reportOwner={reportOwner}
          period={period}
          generatedAt={new Date().toISOString()}
        />
      )}
      {manifestPages.map((page, index) => (
        <ManifestPage
          key={page.id}
          page={page}
          period={period}
          pageNumber={index + 2}
          totalPages={totalPages}
          boardReportId={boardReportId}
          runId={runId}
          narrative={effectiveNarrative}
          drivers={effectiveDrivers}
          metrics={metrics}
          metricsLoading={metricsLoading}
          selectable={selectable}
          selectedBlockId={selectedBlockId}
          selectedBinding={selectedBinding}
          onSelectBlock={onSelectBlock}
        />
      ))}
    </>
  );
};

BoardReportPages.propTypes = {
  boardReport: PropTypes.shape({
    name: PropTypes.string,
    period: PropTypes.string,
    subtitle: PropTypes.string,
    confidentiality: PropTypes.string,
    report_owner: PropTypes.string,
  }),
  run: PropTypes.shape({
    period: PropTypes.string,
    narrative: PropTypes.object,
    drivers: PropTypes.arrayOf(PropTypes.object),
  }),
  manifest: PropTypes.shape({
    pages: PropTypes.arrayOf(PropTypes.object),
  }),
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // SP5 seam - see the component doc comment above.
  narrative: PropTypes.object,
  drivers: PropTypes.arrayOf(PropTypes.object),
  // SP6 seam - see the component doc comment above.
  selectable: PropTypes.bool,
  selectedBlockId: PropTypes.string,
  selectedBinding: PropTypes.object,
  onSelectBlock: PropTypes.func,
  hideCover: PropTypes.bool,
};

export default BoardReportPages;
